import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import type { UserRole } from '@shared/types';

interface InsufficientRoleProps {
  allowedRoles: readonly UserRole[];
}

/**
 * Sprint M cycle 3 (2026-05-15) — F-CYCLE3-P3.
 *
 * Replaces the silent `<Navigate to="/" />` redirect a role-gated route
 * previously fired when the effective role wasn't in `allowedRoles`. A
 * manager hitting /leadership/analytics used to bounce to /manager with
 * zero feedback — looked like a broken link. Now they get an explicit
 * "access restricted" surface with the required role spelled out and a
 * one-click way back to their home.
 *
 * Auth (no user) still redirects to /login in ProtectedRoute — that case
 * is unchanged. This component is ONLY the authenticated-but-wrong-role
 * path.
 */
export function InsufficientRole({ allowedRoles }: InsufficientRoleProps) {
  const roleLabel =
    allowedRoles.length === 1 ? `${allowedRoles[0]} access` : `one of: ${allowedRoles.join(', ')}`;

  return (
    <div className="fw-hero-bg fw-grain bg-fw-bg text-fw-text flex min-h-dvh items-center justify-center px-6">
      <div className="fw-card mx-auto w-full max-w-md p-8 text-center">
        <div className="bg-fw-surface text-fw-accent mx-auto flex h-12 w-12 items-center justify-center rounded-full">
          <ShieldAlert size={22} strokeWidth={1.75} aria-hidden />
        </div>
        <h1 className="text-fw-text mt-5 text-xl font-semibold">Access restricted</h1>
        <p className="text-fw-text-secondary mt-3 text-sm leading-relaxed">
          This view requires {roleLabel}. Your account doesn&rsquo;t have the role for this page. If
          you need it, contact your admin.
        </p>
        <Link
          to="/"
          replace
          className="fw-button-primary mt-6 inline-flex min-h-11 items-center justify-center px-5 py-2.5 text-sm font-medium"
        >
          Back to my home
        </Link>
      </div>
    </div>
  );
}
